import React, { useEffect, useState } from "react";
import { Bell, BellRing, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { pushSupported, getPushState, subscribePush, unsubscribePush, sendTestPush, isIOS, isStandalone } from "@/lib/push";

// Botón para activar/desactivar notificaciones push.
// Si recibe `parentToken`, se suscribe en el contexto del tutor (portal de padres).
export default function PushToggle({ parentToken = null, compact = false, floating = false }) {
  const [state, setState] = useState("loading");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pushSupported()) { setState("unsupported"); return; }
    getPushState(parentToken).then(setState);
  }, [parentToken]);

  if (state === "unsupported") {
    // iOS/iPadOS solo permite Web Push cuando la app está instalada en la pantalla de inicio.
    if (isIOS() && !isStandalone()) {
      const iosHint = () =>
        toast.info(
          "En iPhone/iPad: toca Compartir (el cuadro con la flecha hacia arriba) → \u201CA\u00f1adir a inicio\u201D. Abre la app desde el \u00edcono y ah\u00ed podr\u00e1s activar las notificaciones.",
          { duration: 9000 }
        );
      if (floating) {
        return (
          <button
            type="button"
            data-testid="push-toggle-ios-hint-fab"
            onClick={iosHint}
            title="C\u00f3mo activar notificaciones en iPhone/iPad"
            className="fixed right-4 bottom-[150px] md:bottom-[86px] z-[60] w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95 bg-white text-navy border border-navy/20 shadow-navy/20"
          >
            <BellOff className="w-6 h-6" />
          </button>
        );
      }
      return (
        <button
          type="button"
          data-testid="push-toggle-ios-hint-btn"
          onClick={iosHint}
          title="C\u00f3mo activar notificaciones en iPhone/iPad"
          className={`inline-flex items-center gap-2 rounded-full transition-colors ${
            compact ? "p-2" : "px-3 py-2 text-sm"
          } bg-white/10 text-current hover:bg-white/20 border border-current/20`}
        >
          <BellOff className="w-4 h-4" />
          {!compact && <span>Activar en iPhone</span>}
        </button>
      );
    }
    return null;
  }

  const enable = async () => {
    setBusy(true);
    try {
      await subscribePush(parentToken);
      setState("subscribed");
      toast.success("Notificaciones activadas");
      try { await sendTestPush(parentToken); } catch { /* ignore */ }
    } catch (e) {
      if (e.message === "denied") toast.error("Bloqueaste las notificaciones. Actívalas en los ajustes del navegador.");
      else toast.error("No se pudieron activar las notificaciones.");
      setState(Notification.permission === "denied" ? "denied" : "default");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try { await unsubscribePush(); setState("default"); toast.success("Notificaciones desactivadas"); }
    finally { setBusy(false); }
  };

  const isOn = state === "subscribed";
  const Icon = busy ? Loader2 : (isOn ? BellRing : (state === "denied" ? BellOff : Bell));

  if (floating) {
    return (
      <button
        type="button"
        data-testid="push-toggle-fab"
        onClick={isOn ? disable : enable}
        disabled={busy || state === "loading" || state === "denied"}
        title={state === "denied" ? "Notificaciones bloqueadas en el navegador" : (isOn ? "Notificaciones activadas · toca para desactivar" : "Activar notificaciones")}
        className={`zv-fab-item fixed right-4 bottom-[250px] md:bottom-[156px] z-[60] w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95 disabled:opacity-70 ${
          isOn ? "bg-emerald-500 text-white shadow-emerald-900/30" : "bg-white text-navy border border-navy/20 shadow-navy/20"
        }`}
      >
        <Icon className={`w-6 h-6 ${busy ? "animate-spin" : ""}`} />
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid="push-toggle-btn"
      onClick={isOn ? disable : enable}
      disabled={busy || state === "loading" || state === "denied"}
      title={state === "denied" ? "Notificaciones bloqueadas en el navegador" : (isOn ? "Notificaciones activadas · toca para desactivar" : "Activar notificaciones")}
      className={`inline-flex items-center gap-2 rounded-full transition-colors disabled:opacity-60 ${
        compact ? "p-2" : "px-3 py-2 text-sm"
      } ${isOn ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-white/10 text-current hover:bg-white/20 border border-current/20"}`}
    >
      <Icon className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
      {!compact && <span>{isOn ? "Notificaciones activas" : (state === "denied" ? "Bloqueadas" : "Activar notificaciones")}</span>}
    </button>
  );
}
